import { useState, useMemo } from "react";
import styles from "./Answer.module.css";
import { ProcessStep } from "../../../api";
import MarkdownRenderer from "./AnswerMarkdownRenderer";

interface Props {
  steps: ProcessStep[] | undefined;
}

const REASONING_STEP_TYPES = new Set(["tool_reasoning", "handoff_summary"]);
const isReasoningStep = (step: ProcessStep) =>
  REASONING_STEP_TYPES.has(step.type);

interface ReasoningGroup {
  reasoning: ProcessStep | null;
  tools: ProcessStep[];
}

function groupStepsByReasoning(steps: ProcessStep[]): ReasoningGroup[] {
  const groups: ReasoningGroup[] = [];
  let current: ReasoningGroup = { reasoning: null, tools: [] };

  for (const step of steps) {
    if (isReasoningStep(step)) {
      if (current.reasoning || current.tools.length > 0) {
        groups.push(current);
      }
      current = { reasoning: step, tools: [] };
    } else {
      current.tools.push(step);
    }
  }

  if (current.reasoning || current.tools.length > 0) {
    groups.push(current);
  }

  return groups;
}

export const ProcessStepList = ({ steps }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupStepsByReasoning(steps ?? []), [steps]);

  if (!steps || steps.length === 0) return null;

  // Count total tools across all groups
  const totalTools = groups.reduce((sum, g) => sum + g.tools.length, 0);
  const totalReasoning = groups.filter((g) => g.reasoning).length;

  const toggleGroup = (gi: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(gi) ? next.delete(gi) : next.add(gi);
      return next;
    });
  };

  const toggleTool = (key: string) => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Build summary label
  const summaryParts: string[] = [];
  if (totalReasoning > 0) {
    summaryParts.push(
      `${totalReasoning} reasoning step${totalReasoning !== 1 ? "s" : ""}`
    );
  }
  if (totalTools > 0) {
    summaryParts.push(
      `${totalTools} tool${totalTools !== 1 ? "s" : ""} used`
    );
  }
  const summaryLabel = summaryParts.join(", ");

  return (
    <div className={styles.processStepListWrapper}>
      {/* Top-level collapsed toggle */}
      <div
        className={styles.processStepListToggle}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.processStepListChevron}>
          {isOpen ? "\u25BE" : "\u25B8"}
        </span>
        <span className={styles.processStepListLabel}>{summaryLabel}</span>
      </div>

      {/* Expanded content */}
      {isOpen && (
        <div className={styles.stepsContainer}>
          {groups.map((group, gi) => {
            const toolCount = group.tools.length;
            const isGroupOpen = expandedGroups.has(gi);

            return (
              <div key={gi} className={styles.stepGroup}>
                {group.reasoning && (
                  <div className={styles.reasoningLine}>
                    <MarkdownRenderer
                      decodedContent={group.reasoning.details}
                      isStreaming={false}
                      compact
                    />
                  </div>
                )}

                {toolCount > 0 && (
                  <>
                    <div
                      className={styles.toolGroupToggle}
                      onClick={() => toggleGroup(gi)}
                    >
                      <span className={styles.processStepListChevron}>
                        {isGroupOpen ? "\u25BE" : "\u25B8"}
                      </span>
                      <span className={styles.completedToolLabel}>
                        {toolCount} tool{toolCount !== 1 ? "s" : ""} used
                      </span>
                    </div>

                    {isGroupOpen && (
                      <div className={styles.toolGroupExpanded}>
                        {group.tools.map((tool, ti) => {
                          const toolKey = `${gi}-${ti}`;
                          const isToolOpen = expandedTools.has(toolKey);

                          return (
                            <div key={ti} className={styles.stepGroup}>
                              <div
                                className={styles.stepLine}
                                onClick={() =>
                                  tool.details && toggleTool(toolKey)
                                }
                              >
                                <span className={styles.stepDot} />
                                <span className={styles.stepSummary}>
                                  {tool.label}
                                </span>
                                {tool.details && (
                                  <span
                                    className={`${styles.stepChevron} ${
                                      isToolOpen ? styles.stepChevronOpen : ""
                                    }`}
                                  >
                                    &#x203A;
                                  </span>
                                )}
                              </div>

                              {isToolOpen && tool.details && (
                                <div className={styles.stepDetails}>
                                  <MarkdownRenderer
                                    decodedContent={tool.details}
                                    isStreaming={false}
                                    compact={false}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};